// Mock authentication and organization data
// In production, this would connect to your backend API

import { calculateDaysUntil } from './utils';

// Mock users database (intentionally empty - no demo data)
let users = [];

// Mock organizations database (intentionally empty - no demo data)
let organizations = [];

// Mock organization members database (intentionally empty - no demo data)
let organizationMembers = [];

// Mock invites database
let invites = [];

// Generate unique slug from name
const generateSlug = (name) => {
  let slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  
  // Ensure uniqueness
  let counter = 1;
  let uniqueSlug = slug;
  while (organizations.some(org => org.slug === uniqueSlug)) {
    uniqueSlug = `${slug}-${counter}`;
    counter++;
  }
  return uniqueSlug;
};

// Generate random token
const generateToken = () => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

// Auth functions
export const mockAuthService = {
  // Sign up new user
  signup: async (email, password, name) => {
    // Check if user exists
    if (users.find(u => u.email === email)) {
      throw new Error('User with this email already exists');
    }

    const newUser = {
      id: `user-${Date.now()}`,
      email,
      name,
      password, // In production, hash this
      created_at: new Date().toISOString()
    };

    users.push(newUser);
    
    // Store in localStorage
    localStorage.setItem('users', JSON.stringify(users));
    localStorage.setItem('currentUser', JSON.stringify(newUser));
    
    return { user: newUser };
  },

  // Login
  login: async (email, password) => {
    const user = users.find(u => u.email === email && u.password === password);
    
    if (!user) {
      throw new Error('Invalid email or password');
    }

    localStorage.setItem('currentUser', JSON.stringify(user));
    return { user };
  },

  // Logout
  logout: () => {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('activeOrgId');
  },

  // Get current user
  getCurrentUser: () => {
    const userJson = localStorage.getItem('currentUser');
    return userJson ? JSON.parse(userJson) : null;
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('currentUser');
  }
};

// Organization functions
export const mockOrgService = {
  // Create organization (only for new signups)
  createOrganization: async (name, userId, currency = 'INR', timezone = 'Asia/Kolkata') => {
    const slug = generateSlug(name);
    
    const newOrg = {
      id: `org-${Date.now()}`,
      name,
      slug,
      created_by: userId,
      currency,
      timezone,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    organizations.push(newOrg);

    // Create membership for creator as org_admin
    const membership = {
      id: `member-${Date.now()}`,
      organization_id: newOrg.id,
      user_id: userId,
      role: 'org_admin',
      status: 'active',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    organizationMembers.push(membership);

    // Store in localStorage
    localStorage.setItem('organizations', JSON.stringify(organizations));
    localStorage.setItem('organizationMembers', JSON.stringify(organizationMembers));
    localStorage.setItem('activeOrgId', newOrg.id);

    return { organization: newOrg, membership };
  },

  // Get user's organizations
  getUserOrganizations: (userId) => {
    const memberships = organizationMembers.filter(
      m => m.user_id === userId && m.status === 'active'
    );
    
    return memberships.map(m => {
      const org = organizations.find(o => o.id === m.organization_id);
      return {
        ...org,
        role: m.role,
        membership_id: m.id
      };
    });
  },

  // Get organization by ID
  getOrganization: (orgId) => {
    return organizations.find(o => o.id === orgId);
  },

  // Get user's role in organization
  getUserRole: (userId, orgId) => {
    const membership = organizationMembers.find(
      m => m.user_id === userId && m.organization_id === orgId && m.status === 'active'
    );
    return membership?.role || null;
  },

  // Get organization members
  getOrganizationMembers: (orgId) => {
    const members = organizationMembers.filter(m => m.organization_id === orgId);
    
    return members.map(m => {
      const user = users.find(u => u.id === m.user_id);
      const invite = invites.find(i => i.id === m.invite_id || (i.email === m.email && !m.user_id));
      
      return {
        userId: m.user_id || invite?.email || 'unknown',
        name: user?.name || invite?.name || m.name || invite?.email?.split('@')[0] || 'Pending',
        email: user?.email || invite?.email || m.email || '',
        phone: user?.phone || invite?.phone || m.phone || '',
        role: m.role,
        status: m.status || (invite ? 'invited' : 'active')
      };
    });
  },

  // Invite member
  inviteMember: async (orgId, email, role, invitedBy, phone = '', name = '') => {
    if (!phone || !phone.trim()) {
      throw new Error('Phone number is required');
    }
    if (!name || !name.trim()) {
      throw new Error('Name is required');
    }

    // Check if user already exists
    const existingUser = users.find(u => u.email === email);
    
    // Check if already a member
    if (existingUser) {
      const existingMembership = organizationMembers.find(
        m => m.user_id === existingUser.id && m.organization_id === orgId
      );
      
      if (existingMembership) {
        throw new Error('User is already a member of this organization');
      }
    }

    const token = generateToken();
    const invite = {
      id: `invite-${Date.now()}`,
      organization_id: orgId,
      email,
      phone,
      name,
      role,
      token,
      invited_by: invitedBy,
      status: 'pending',
      created_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
    };

    invites.push(invite);

    // Create pending membership
    const membership = {
      id: `member-${Date.now()}`,
      organization_id: orgId,
      user_id: null,
      role,
      status: 'invited',
      phone,
      name,
      invite_id: invite.id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    organizationMembers.push(membership);

    // Store in localStorage
    localStorage.setItem('invites', JSON.stringify(invites));
    localStorage.setItem('organizationMembers', JSON.stringify(organizationMembers));

    return { invite, inviteUrl: `/accept-invite?token=${token}` };
  },

  // Get invite by token
  getInviteByToken: (token) => {
    const invite = invites.find(i => i.token === token && i.status === 'pending');
    
    if (!invite) return null;

    // Check if expired
    if (new Date(invite.expires_at) < new Date()) {
      return null;
    }

    const org = organizations.find(o => o.id === invite.organization_id);
    
    return {
      ...invite,
      organization_name: org?.name
    };
  },

  // Accept invite
  acceptInvite: async (token, userId) => {
    const invite = invites.find(i => i.token === token && i.status === 'pending');
    
    if (!invite) {
      throw new Error('Invalid or expired invite');
    }

    // Check if expired
    if (new Date(invite.expires_at) < new Date()) {
      throw new Error('This invite has expired');
    }

    // Find the pending membership
    const membership = organizationMembers.find(
      m => m.invite_id === invite.id && m.status === 'invited'
    );

    if (!membership) {
      throw new Error('Invite membership not found');
    }

    // Update membership
    membership.user_id = userId;
    membership.status = 'active';
    membership.updated_at = new Date().toISOString();

    // Mark invite as accepted
    invite.status = 'accepted';
    invite.accepted_at = new Date().toISOString();
    invite.accepted_by = userId;

    // Store in localStorage
    localStorage.setItem('invites', JSON.stringify(invites));
    localStorage.setItem('organizationMembers', JSON.stringify(organizationMembers));
    localStorage.setItem('activeOrgId', invite.organization_id);

    const org = organizations.find(o => o.id === invite.organization_id);
    
    return { organization: org, membership };
  },

  // Update member role
  updateMemberRole: async (memberId, newRole, updatedBy) => {
    const membership = organizationMembers.find(m => m.id === memberId);
    
    if (!membership) {
      throw new Error('Member not found');
    }

    // Check if trying to remove last admin
    if (membership.role === 'org_admin' && newRole !== 'org_admin') {
      const adminCount = organizationMembers.filter(
        m => m.organization_id === membership.organization_id && 
             m.role === 'org_admin' && 
             m.status === 'active'
      ).length;

      if (adminCount <= 1) {
        throw new Error('Cannot remove the last admin. Promote another member to admin first.');
      }
    }

    membership.role = newRole;
    membership.updated_at = new Date().toISOString();

    localStorage.setItem('organizationMembers', JSON.stringify(organizationMembers));
    
    return { membership };
  },

  // Remove member
  removeMember: async (memberId, removedBy) => {
    const memberIndex = organizationMembers.findIndex(m => m.id === memberId);
    
    if (memberIndex === -1) {
      throw new Error('Member not found');
    }

    const membership = organizationMembers[memberIndex];

    // Check if trying to remove last admin
    if (membership.role === 'org_admin') {
      const adminCount = organizationMembers.filter(
        m => m.organization_id === membership.organization_id && 
             m.role === 'org_admin' && 
             m.status === 'active'
      ).length;

      if (adminCount <= 1) {
        throw new Error('Cannot remove the last admin');
      }
    }

    membership.status = 'disabled';
    membership.updated_at = new Date().toISOString();

    localStorage.setItem('organizationMembers', JSON.stringify(organizationMembers));
    
    return { success: true };
  }
};

// Permission helper functions
// Supports four roles: org_admin, admin, planner, client
// org_admin has the highest permissions and can override all restrictions
export const permissions = {
  // Wedding management
  canCreateWeddings: (role) => ['org_admin', 'admin'].includes(role),
  canEditWeddings: (role) => ['org_admin', 'admin', 'planner'].includes(role),
  canDeleteWeddings: (role) => ['org_admin', 'admin'].includes(role),
  
  // Team management
  canInviteMembers: (role) => ['org_admin', 'admin'].includes(role),
  canManageMembers: (role) => ['org_admin', 'admin'].includes(role),
  canManageOrgAdmin: (role) => role === 'org_admin', // Only org_admin can manage other org_admins
  
  // Module access (vendors, tasks, budget, menu, events)
  canManageVendors: (role) => ['org_admin', 'admin', 'planner'].includes(role),
  canManageTasks: (role) => ['org_admin', 'admin', 'planner'].includes(role),
  canManageBudget: (role) => ['org_admin', 'admin', 'planner'].includes(role),
  canManageMenu: (role) => ['org_admin', 'admin', 'planner'].includes(role),
  canManageEvents: (role) => ['org_admin', 'admin', 'planner'].includes(role),
  canManageGuests: (role) => ['org_admin', 'admin', 'planner'].includes(role),
  
  // Organization settings
  canEditOrganization: (role) => ['org_admin', 'admin'].includes(role),
  canManageOrganizationExclusive: (role) => role === 'org_admin', // Only org_admin for critical org operations
  
  // Override capabilities (org_admin only)
  canOverrideRestrictions: (role) => role === 'org_admin', // Can override guest_list_locked, etc.
  
  // Read-only check
  isReadOnly: (role) => role === 'client' || role === 'read_only_client', // Legacy support
  
  // Role checks
  isOrgAdmin: (role) => role === 'org_admin',
  isAdmin: (role) => role === 'admin',
  isPlanner: (role) => role === 'planner',
  isClient: (role) => role === 'client' || role === 'read_only_client' // Legacy support
};

// Initialize from localStorage on load
if (typeof window !== 'undefined') {
  const storedUsers = localStorage.getItem('users');
  const storedOrgs = localStorage.getItem('organizations');
  const storedMembers = localStorage.getItem('organizationMembers');
  const storedInvites = localStorage.getItem('invites');

  if (storedUsers) users = JSON.parse(storedUsers);
  if (storedOrgs) organizations = JSON.parse(storedOrgs);
  if (storedMembers) organizationMembers = JSON.parse(storedMembers);
  if (storedInvites) invites = JSON.parse(storedInvites);
}